const { orderedItems } = require("../quote-line-structure");
const { STRUCTURE_KINDS, CLIENT_DISPLAYS, ECONOMIC_LINE_TYPES, DESCRIPTIVE_LINE_TYPES,
  QUANTITY_LIMITS, ABANDONED_FIELDS, object, own, id, scopeKey, copyScope, issue, result } = require("./contracts");
const { parse, format, multiplyQuantity, storedDecimal } = require("./decimal");

const nonzero = (value) => {
  if (value == null) return false;
  const decimal = parse(value, { signed: true });
  return decimal === null || decimal.coefficient !== 0n;
};
const validateHierarchy = (source, { quantityLimits = QUANTITY_LIMITS } = {}) => {
  const errors = [];
  if (!object(source) || !Array.isArray(source.lines) || id(source.quoteVersionId) === null
    || scopeKey(source.scope) === null) {
    return result([issue("QUOTE_COMPOSITION_INVALID", "Version, périmètre et liste de lignes requis.")], { graph: null });
  }
  const limits = { ...QUANTITY_LIMITS, ...quantityLimits };
  if (![limits.localPrecision, limits.localScale, limits.derivedPrecision, limits.derivedScale]
    .every((n) => Number.isSafeInteger(n) && n >= 0 && n <= 1000)
    || limits.localPrecision !== 14 || limits.localScale !== 4
    || limits.derivedPrecision < 1 || !parse(limits.maxMagnitude)) {
    return result([issue("QUOTE_COMPOSITION_INVALID", "Capacités décimales invalides.")], { graph: null });
  }
  const versionId = id(source.quoteVersionId), key = scopeKey(source.scope);
  const nodes = new Map(), quantities = new Map(), sections = new Map();
  const add = (code, message, line, index, field, status = 400) => errors.push(issue(code, message, {
    lineId: id(line?.id), parentLineId: id(line?.parentLineId),
    path: ["lines", index, field], scope: copyScope(source.scope),
  }, status));

  if (source.sections !== undefined && !Array.isArray(source.sections)) {
    errors.push(issue("QUOTE_COMPOSITION_INVALID", "Liste de sections invalide.", { path: ["sections"] }));
  }
  for (const [index, section] of (Array.isArray(source.sections) ? source.sections : []).entries()) {
    const sectionId = id(section?.id);
    if (!object(section) || sectionId === null || sections.has(sectionId)
      || id(section.quoteVersionId) !== versionId || scopeKey(section.scope) !== key
      || (section.isClientVisible !== undefined && typeof section.isClientVisible !== "boolean")) {
      errors.push(issue("QUOTE_COMPOSITION_INVALID", "Identité, version, périmètre ou visibilité de section invalide.",
        { path: ["sections", index], sectionId }));
      continue;
    }
    const parent = section.parentSectionId == null ? null : id(section.parentSectionId);
    if (section.parentSectionId != null && parent === null) {
      errors.push(issue("QUOTE_COMPOSITION_INVALID", "Identité de section parente invalide.", { sectionId }));
    }
    sections.set(sectionId, { id: sectionId, quoteVersionId: versionId, scope: copyScope(source.scope), parentSectionId: parent,
      isClientVisible: section.isClientVisible !== false });
  }
  // Both section and line ancestry are traversed iteratively, never recursively.
  const sectionVisible = new Map();
  for (const section of sections.values()) {
    const path = [], visiting = new Set();
    let cursor = section.id;
    while (cursor !== null && !sectionVisible.has(cursor)) {
      if (visiting.has(cursor) || !sections.has(cursor)) {
        errors.push(issue("QUOTE_COMPOSITION_INVALID", "Cycle ou parent absent dans les sections.", { sectionId: cursor }));
        break;
      }
      visiting.add(cursor); path.push(cursor); cursor = sections.get(cursor).parentSectionId;
    }
    let visible = cursor === null ? true : sectionVisible.get(cursor) === true;
    for (let j = path.length - 1; j >= 0; j -= 1) {
      visible = visible && sections.get(path[j]).isClientVisible;
      sectionVisible.set(path[j], visible);
    }
  }

  source.lines.forEach((line, index) => {
    if (!object(line) || id(line.id) === null) {
      add("QUOTE_COMPOSITION_ID_REQUIRED", "Identifiant de ligne requis.", line, index, "id"); return;
    }
    const lineId = id(line.id);
    if (nodes.has(lineId)) { add("QUOTE_COMPOSITION_DUPLICATE_ID", "Identifiant de ligne dupliqué.", line, index, "id"); return; }
    if (id(line.quoteVersionId) !== versionId) add("QUOTE_COMPOSITION_VERSION_MISMATCH", "Ligne d'une autre version.", line, index, "quoteVersionId");
    if (scopeKey(line.scope) !== key) add("QUOTE_COMPOSITION_SCOPE_MISMATCH", "Ligne d'un autre périmètre.", line, index, "scope");
    if (id(line.quoteSectionId) === null || (Array.isArray(source.sections) && !sections.has(id(line.quoteSectionId)))) {
      add("QUOTE_COMPOSITION_SECTION_MISMATCH", "Section de ligne absente du périmètre.", line, index, "quoteSectionId");
    }
    if (!STRUCTURE_KINDS.includes(line.structureKind)) add("QUOTE_COMPOSITION_INVALID", "Type structurel inconnu.", line, index, "structureKind");
    if (line.parentLineId !== null && line.parentLineId !== undefined && id(line.parentLineId) === null) add("QUOTE_COMPOSITION_INVALID", "Identité du parent invalide.", line, index, "parentLineId");
    if (typeof line.unit !== "string" || !line.unit.trim() || (line.structureKind === "composite" && line.unit !== "Ens")) {
      add("QUOTE_COMPOSITION_INVALID", "Unité requise ; un composite utilise Ens.", line, index, "unit");
    }
    if (line.structureKind === "simple" && line.unit === "Ens") add("QUOTE_COMPOSITION_UPGRADE_REQUIRED", "Une ancienne ligne simple Ens exige une mise à niveau explicite avant le contrat v2.", line, index, "unit", 409);
    if (line.structureKind === "composite" ? !CLIENT_DISPLAYS.includes(line.clientCompositionDisplay) : line.clientCompositionDisplay != null) {
      add("QUOTE_COMPOSITION_INVALID", "Affichage de composition absent, inconnu ou actif sur une ligne simple.", line, index, "clientCompositionDisplay");
    }
    if (line.isClientVisible !== undefined && typeof line.isClientVisible !== "boolean") add("QUOTE_CLIENT_VISIBILITY_UNVERIFIABLE", "Visibilité de ligne invalide.", line, index, "isClientVisible", 409);
    const lineType = line.lineType ?? "service";
    if (![...ECONOMIC_LINE_TYPES, ...DESCRIPTIVE_LINE_TYPES].includes(lineType)
      || (line.structureKind === "composite" && lineType !== "service")
      || (line.parentLineId != null && DESCRIPTIVE_LINE_TYPES.includes(lineType))) {
      add("QUOTE_COMPOSITION_INVALID", "Type de ligne incompatible avec une composition économique.", line, index, "lineType");
    }
    if (line.orderIndex != null && !((typeof line.orderIndex === "number" && Number.isSafeInteger(line.orderIndex))
      || (typeof line.orderIndex === "string" && /^-?\d+$/.test(line.orderIndex) && Number.isSafeInteger(Number(line.orderIndex))))) {
      add("QUOTE_COMPOSITION_INVALID", "Ordre local invalide.", line, index, "orderIndex");
    }
    const quantity = storedDecimal(line.quantity, 14, 4);
    if (!quantity) add("QUOTE_COMPOSITION_INVALID_QUANTITY", "Quantité locale positive ou nulle, DECIMAL(14,4), requise sans arrondi.", line, index, "quantity");
    else quantities.set(lineId, quantity);
    if (ABANDONED_FIELDS.some((field) => own(line, field))) add("QUOTE_COMPOSITION_SCHEMA_MISMATCH", "Le contrat de prix indépendant est abandonné.", line, index, "structureKind");
    if (!["calculated", "forced"].includes(line.sellingPriceMode)) add("QUOTE_COMPOSITION_INVALID", "Mode de prix inconnu.", line, index, "sellingPriceMode");
    const forcedPrice = line.forcedUnitSellingPrice == null ? null : storedDecimal(line.forcedUnitSellingPrice, 14, 4);
    if ((line.sellingPriceMode === "forced" && !forcedPrice) || (line.forcedUnitSellingPrice != null && !forcedPrice)) {
      add("QUOTE_FORCING_VALUE_INVALID", "PU HT forcé invalide ou absent ; aucune normalisation de commande n'est faite en B1.", line, index, "forcedUnitSellingPrice");
    }
    if (DESCRIPTIVE_LINE_TYPES.includes(lineType) && line.sellingPriceMode === "forced") add("QUOTE_COMPOSITION_INVALID", "Une ligne descriptive ne porte pas de prix forcé.", line, index, "sellingPriceMode");
    for (const field of ["laborItems", "materials", "subcontractingItems", "costComponents", "cost_components", "labor_items", "subcontracting_items"]) {
      if (line[field] !== undefined && !Array.isArray(line[field])) add("QUOTE_COMPOSITION_INVALID", "Liste de composants invalide.", line, index, field);
      else if (line.structureKind === "composite" && line[field]?.length) add("QUOTE_COMPOSITION_ADJUSTMENT_INVALID", "Un composite ne porte pas de composants économiques directs.", line, index, field);
    }
    if (line.structureKind === "composite") {
      // ORM defaults in inherited/reference fields are not active overrides.
      const activeFields = ["otherDirectCost", "purchaseCost", "subcontractingCost", "estimatedHours", "laborRate"];
      if (activeFields.some((field) => nonzero(line[field])) || line.discountValue != null || line.discount_value != null || line.discount != null
        || (line.coefficientMode != null && line.coefficientMode !== "inherited")
        || line.manualCoefficient != null || line.forcedCoefficient != null || line.manual_coefficient != null
        || line.forced_coefficient != null || line.natureCoefficientOverrides?.length) {
        add("QUOTE_COMPOSITION_ADJUSTMENT_INVALID", "Coût, remise locale ou coefficient actif incompatible sur un composite.", line, index, "structureKind");
      }
    }
    const normalized = {
      id: lineId, quoteVersionId: versionId, quoteSectionId: id(line.quoteSectionId),
      scope: copyScope(source.scope), parentLineId: line.parentLineId == null ? null : id(line.parentLineId),
      structureKind: line.structureKind, lineType, quantity: quantity ? format(quantity) : null, unit: line.unit,
      orderIndex: line.orderIndex ?? null, isClientVisible: line.isClientVisible !== false,
      clientCompositionDisplay: line.clientCompositionDisplay ?? null,
      sellingPriceMode: line.sellingPriceMode, forcedUnitSellingPrice: forcedPrice ? format(forcedPrice) : null,
      vatRate: line.structureKind === "simple" && ["string", "number"].includes(typeof line.vatRate) ? line.vatRate : undefined,
      childIds: [], sourceIndex: index,
    };
    // Only known descriptive scalars survive normalization, never arbitrary financial objects.
    for (const field of ["code", "description", "lineComment"]) if (typeof line[field] === "string") normalized[field] = line[field];
    nodes.set(lineId, normalized);
  });
  const roots = [];
  for (const node of nodes.values()) {
    const parent = nodes.get(node.parentLineId);
    if (node.parentLineId === null) { roots.push(node); continue; }
    if (!parent) add("QUOTE_COMPOSITION_PARENT_NOT_FOUND", "Parent inexistant dans ce périmètre.", node, node.sourceIndex, "parentLineId");
    else if (parent.id === node.id) add("QUOTE_COMPOSITION_CYCLE", "Auto-parentage interdit.", node, node.sourceIndex, "parentLineId");
    else {
      if (parent.structureKind !== "composite") add("QUOTE_COMPOSITION_INVALID_PARENT", "Le parent doit être composite.", node, node.sourceIndex, "parentLineId");
      if (parent.quoteSectionId !== node.quoteSectionId) add("QUOTE_COMPOSITION_SECTION_MISMATCH", "Parent et enfant doivent partager la même section.", node, node.sourceIndex, "quoteSectionId");
      parent.childIds.push(node.id);
    }
  }
  // Three-color parent walk finds cycles even when no root exists.
  const done = new Set();
  for (const node of nodes.values()) {
    const path = [], visiting = new Set(); let cursor = node;
    while (cursor && !done.has(cursor.id)) {
      if (visiting.has(cursor.id)) {
        if (cursor.parentLineId !== cursor.id) add("QUOTE_COMPOSITION_CYCLE", "Cycle indirect de composition.", cursor, cursor.sourceIndex, "parentLineId");
        break;
      }
      path.push(cursor.id); visiting.add(cursor.id); cursor = nodes.get(cursor.parentLineId);
    }
    for (const nodeId of path) done.add(nodeId);
  }
  if (errors.length) return result(errors, { graph: null });
  for (const node of nodes.values()) node.childIds = orderedItems(node.childIds.map((childId) => nodes.get(childId))).map((child) => child.id);
  const rootIds = orderedItems(roots).map((node) => node.id);
  const traversal = [], stack = [...rootIds].reverse().map((nodeId) => ({ nodeId, ancestor: { coefficient: 1n, scale: 0 }, protectedBy: null }));
  while (stack.length) {
    const { nodeId, ancestor, protectedBy } = stack.pop(), node = nodes.get(nodeId);
    const effective = ancestor && multiplyQuantity(ancestor, quantities.get(nodeId), limits);
    if (!effective) add("QUOTE_COMPOSITION_INVALID_QUANTITY", "Capacité décimale dépassée par les multiplicateurs ; aucun arrondi autorisé.", node, node.sourceIndex, "quantity");
    node.ancestorMultiplier = ancestor ? format(ancestor) : null;
    node.effectiveQuantity = effective ? format(effective) : null;
    node.financialRole = node.parentLineId === null ? "root" : "subitem";
    node.protectedByNodeId = protectedBy;
    node.economic = node.structureKind === "simple" && ECONOMIC_LINE_TYPES.includes(node.lineType);
    node.activeCommercialBoundary = protectedBy === null && (node.economic || node.sellingPriceMode === "forced");
    traversal.push(nodeId);
    for (let j = node.childIds.length - 1; j >= 0; j -= 1) stack.push({ nodeId: node.childIds[j], ancestor: effective,
      protectedBy: protectedBy ?? (node.sellingPriceMode === "forced" ? node.id : null) });
  }
  const leafCounts = new Map(), incompleteIds = [];
  for (let j = traversal.length - 1; j >= 0; j -= 1) {
    const node = nodes.get(traversal[j]);
    const count = node.economic ? 1 : node.childIds.reduce((sum, childId) => sum + leafCounts.get(childId), 0);
    leafCounts.set(node.id, count);
    if (node.structureKind === "composite" && count === 0) incompleteIds.push(node.id);
  }
  if (errors.length) return result(errors, { graph: null });
  return result([], { graph: { quoteVersionId: versionId, scope: copyScope(source.scope),
    nodes: traversal.map((nodeId) => nodes.get(nodeId)), rootIds,
    sections: [...sections.values()].map((section) => ({ ...section, effectiveVisibility: sectionVisible.get(section.id) })),
    sectionsProvided: Array.isArray(source.sections), complete: incompleteIds.length === 0,
    incompleteIds: incompleteIds.reverse() } });
};

module.exports = { validateHierarchy };
